
import Vue from "vue";
import { ViewAssessmentsOptions } from "@/interfaces/candidate/canidate_assessments";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  APP_ASSESSMENT_SS,
  APP_ENG_ASSESSMENT_CANCELLED,
  APP_ENG_ASSESSMENT_COMPLETED,
  APP_ENG_CURRENT_QUESTION_INDEX,
  ASSESSMENT_QUESTIONNAIRES,
  ASSESSMENT_QUESTIONNAIRES_ENGLISH
} from "@/store/modules/candidates/constants";
import InterviewLoading from "@/components/candidate/interviews/InterviewLoading.vue";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import AppEngAssessmentRoot from "@/components/candidate/app_assessments/eng_assessment/AppEngAssessmentRoot.vue";
import {
  APP_ASSESSMENTS,
  APP_ASSESSMENT_RESULT,
  MARK_ASSESSMENT_RESUMED
} from "@/store/modules/common/constants";
import AppAssessmentTermConditions from "@/components/AppAssessmentTermConditions.vue";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { get_random_number_between, wait_until } from "@/utils/global";
import AppResourseNotFound from "@/components/candidate/app_assessments/AppResourseNotFound.vue";
import {
  GET_COMPANY_DETAILS,
  GET_USER_DETAILS
} from "@/store/modules/auth/constants";
import { resolveEnglishAssessmentTypeByPersona } from "@/utils/assessment_persona";
import store from "@/store";
import i18n from "@/i18n";
import router from "@/router";
import socketService from "@/services/socket.service";
import { SocketEvents } from "@/interfaces/app.interface";
import domtoimage from "dom-to-image-more";
import {
  request_assessment_resume,
  cancel_my_assessment_attempt
} from "@/store/apis/recruiter";

interface AssessmentResultWithScore {
  overall_score?: number;
  result?: { overall_score?: number };
  resume_admin_status?: string | null;
}

export default Vue.extend({
  name: "InceptionEnglishAssessmentIndex",
  components: {
    AppAssessmentTermConditions,
    InterviewLoading,
    AppEngAssessmentRoot,
    AppResourseNotFound
  },
  data() {
    return {
      stage: "",
      data_loading: false,
      data_error: "" as string,
      term_condition_accepted: false,
      screenshot_taken: 0,
      assessment_already_completed: false,
      screen_shots: [] as string[],
      ss_random_number: 5,
      is_reload: false,
      assessment_id: 1,
      leave_dialog_visible: false,
      leave_dialog_resolve: null as ((confirmed: boolean) => void) | null,
      leave_dialog_confirmed: false,
      assessment_completed: false,
      beforeUnloadHandler: null as ((event: BeforeUnloadEvent) => void) | null
    };
  },
  async created() {
    // this.ss_random_number = get_random_number_between(1, 5);
    this.term_condition_accepted =
      localStorage.getItem("eng-terms-accepted") === "1" ? true : false;

    if (this.term_condition_accepted) {
      this.process_term_condition(true);
    }
  },
  mounted() {
    // Best-effort safeguard for hard leaves (tab close, reload, direct URL)
    // so that unfinished attempts are sent for admin approval instead of
    // being treated as submitted.
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      const win = window as unknown as {
        __englishAssessmentSubmitted?: boolean;
      };
      const justSubmitted = Boolean(win.__englishAssessmentSubmitted);

      const assessmentIsActive =
        this.term_condition_accepted &&
        !this.assessment_cancelled &&
        !justSubmitted;

      if (assessmentIsActive && this.assessment_id) {
        try {
          // Fire-and-forget – backend will mark this attempt as
          // "Admin Approval Required" for any non-submitted exit.
          request_assessment_resume(this.assessment_id);
        } catch (_e) {
          // ignore – best-effort only
        }
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  },
  watch: {
    screen_shots(val: string[]) {
      // If screen shots are more than 15 then send the screen shots to server
      if (val.length > this.ss_random_number) {
        const bucket_prefix =
          this.get_company_details?.storage_path?.assessments_path
            ?.split("/")
            .slice(1)
            .join("/") ?? "open/assessments/inception"; // Bucket prefix
        const assessment_id = this.assessment_id;
        const file_name = `user_${this.get_user_details.id}_${assessment_id}_image.png`; // File name
        const assessment_type = resolveEnglishAssessmentTypeByPersona(
          this.get_user_details?.persona,
          this.get_company_details?.storage_path?.assessment_setting
            ?.candidates_english
        ); // Assessment type
        // Emitting event to upload screen shots
        socketService.socket.emit(SocketEvents.UPLOAD_SCREEN_SHOTS, {
          screen_shots: val,
          user_id: this.get_user_details.id,
          assessment_id,
          bucket_prefix,
          assessment_type: assessment_type ? assessment_type : "unknown",
          file_name,
          completed: this.assessment_completed
          // cancelled: this.assessment_cancelled
        });
        this.screen_shots = [];
      }
    }
  },
  computed: {
    ...mapGetters("candidate", {
      app_eng_assessments: ASSESSMENT_QUESTIONNAIRES_ENGLISH,
      current_question_index: APP_ENG_CURRENT_QUESTION_INDEX,
      assessment_completed: APP_ENG_ASSESSMENT_COMPLETED,
      assessment_cancelled: APP_ENG_ASSESSMENT_CANCELLED
    }),
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS
    }),
    ...mapGetters("auth", {
      get_company_details: GET_COMPANY_DETAILS,
      get_user_details: GET_USER_DETAILS
    }),
    ViewAssessmentsOptions() {
      return ViewAssessmentsOptions;
    }
  },
  methods: {
    ...mapActions("candidate", {
      fetch_assessment_questionnaries: ASSESSMENT_QUESTIONNAIRES
    }),
    ...mapMutations("candidate", {
      set_app_eng_assessments: ASSESSMENT_QUESTIONNAIRES_ENGLISH,
      update_assessment_cancelled: APP_ENG_ASSESSMENT_CANCELLED,
      update_assessment_completed: APP_ENG_ASSESSMENT_COMPLETED,
      upload_assessment_screenshot: APP_ASSESSMENT_SS
    }),
    ...mapActions("common", {
      app_assessment_result: APP_ASSESSMENT_RESULT
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION,
      set_root_error: ROOT_ERROR
    }),
    /**
     *  Function to process term condition accepted or not accepted,
     * if accepted then init function is called to fetch assessment questionnaries
     * else user is redirected to dashboard
     * @param val boolean value to check if term condition is accepted or not
     */
    async process_term_condition(val: boolean) {
      if (!val) {
        // Denying terms = assessment not started; clear any in-progress attempt so dashboard shows "Start assessment" not "Wait for Approval"
        localStorage.removeItem("eng-terms-accepted");
        const assessmentId = this.getEnglishAssessmentId();
        if (assessmentId != null) {
          try {
            await cancel_my_assessment_attempt(assessmentId);
          } catch (_e) {
            // Non-blocking: still redirect so user sees dashboard
          }
        }
        await this.$router.push("/candidate/dashboard");
      } else {
        this.term_condition_accepted = true;
        localStorage.setItem("eng-terms-accepted", "1");
        await this.init();
        // Screenshot capture
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (
            this.assessment_cancelled ||
            this.assessment_completed ||
            !this.term_condition_accepted
          ) {
            break;
          }
          const randomNumber = get_random_number_between(8, 12);
          await wait_until(randomNumber * 1000);
          const img = await domtoimage.toPng(document.body);
          this.screen_shots.push(img);
        }
      }
    },
    /** Resolve English assessment id (same logic as init) for cancel/API calls */
    getEnglishAssessmentId(): number | null {
      const assessment_setting = resolveEnglishAssessmentTypeByPersona(
        this.get_user_details?.persona,
        this.get_company_details?.storage_path?.assessment_setting
          ?.candidates_english
      );
      const assessment_setting_id = Number(assessment_setting);
      const has_numeric_assessment_setting = Number.isFinite(
        assessment_setting_id
      );

      let assessment = this.app_assessments.find(
        (val: AppAssessments.Assessment) =>
          val.assessment_type === assessment_setting ||
          (has_numeric_assessment_setting && val.id === assessment_setting_id)
      );

      if (!assessment) {
        const englishAssessments = this.app_assessments.filter(
          (val: AppAssessments.Assessment) =>
            val.assessment_name?.toLowerCase().includes("english") ||
            val.assessment_type?.toLowerCase().includes("english")
        );
        if (englishAssessments.length > 0) {
          const preferOrder = ["english_v5", "english_v4", "english_v3"];
          assessment =
            preferOrder
              .map((t) =>
                englishAssessments.find(
                  (a: AppAssessments.Assessment) => a.assessment_type === t
                )
              )
              .find(Boolean) ||
            [...englishAssessments].sort((a, b) => b.id - a.id)[0];
        }
      }

      if (!assessment && this.app_assessments.length > 0) {
        assessment = this.app_assessments[0];
      }

      return assessment ? assessment.id : null;
    },
    /**
     * Function to initialize assessment test by fetching assessment questionnaries
     */
    async init() {
      this.data_loading = true; // Loading until data is fetched
      this.data_error = "";

      const assessmentId = this.getEnglishAssessmentId();
      if (assessmentId == null) {
        this.data_error = this.$t("assessments.not-found").toString();
        this.data_loading = false;
        return;
      }

      const assessment = this.app_assessments.find(
        (val: AppAssessments.Assessment) => val.id === assessmentId
      );
      if (!assessment) {
        this.data_error = this.$t("assessments.not-found").toString();
        this.data_loading = false;
        return;
      }
      // If assessment already exist: return
      const assessment_exist = await this.app_assessment_result({
        assessment_id: assessment.id
      });
      this.assessment_id = assessment.id;
      if (
        assessment_exist &&
        assessment_exist.status !==
          AppAssessments.AppAssessmentStatus.IN_PROGRESS
      ) {
        this.set_root_notification(
          `${this.$t("assessments.completed-already")}`
        );
        this.assessment_already_completed = true;
        // Clear the terms flag immediately so that beforeRouteLeave does not
        // treat this as an "active assessment" and show the Leave dialog.
        localStorage.removeItem("eng-terms-accepted");
        localStorage.removeItem("stage");
        this.term_condition_accepted = false;
        await this.$router.push("/candidate/dashboard");
        this.data_loading = false;
        return;
      }
      // Fetching assessment questionnaries
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await this.fetch_assessment_questionnaries({
        assessment_id: assessment.id
      });
      if (!response) {
        this.data_error = this.$t("assessments.failed-to-get").toString();
        this.data_loading = false;
        return;
      }
      // Normalize question_options (API may return as object or JSON string)
      const getFilter = (val: AppAssessments.AssessmentQuestionnaires) => {
        let opts = val.question_options;
        if (typeof opts === "string") {
          try {
            opts = JSON.parse(opts) as AppAssessments.QuestionOptions;
          } catch {
            return undefined;
          }
        }
        return opts?.filter ?? (opts as { filter_ml?: string })?.filter_ml;
      };
      const matchFilter = (
        val: AppAssessments.AssessmentQuestionnaires,
        expected: string
      ) => {
        const filter = getFilter(val);
        if (!filter) return false;
        // Backend may send "listening_questions" or "LISTENING_QUESTIONS"
        const f = String(filter).toLowerCase();
        const e = expected.toLowerCase();
        // Exact match or prefix match (e.g. "speaking" matches "speaking_task_a")
        return filter === expected || f === e || f.startsWith(e);
      };
      const speak_response = response.filter(
        (val: AppAssessments.AssessmentQuestionnaires) =>
          matchFilter(val, AppAssessments.QuestionFilter.SPEAKING)
      );
      const mcqs_response = response.filter(
        (val: AppAssessments.AssessmentQuestionnaires) =>
          matchFilter(val, AppAssessments.QuestionFilter.MCQS)
      );
      const written_response = response.filter(
        (val: AppAssessments.AssessmentQuestionnaires) =>
          matchFilter(val, AppAssessments.QuestionFilter.WRITTEN)
      );
      const reading_questions_response = response.filter(
        (val: AppAssessments.AssessmentQuestionnaires) =>
          matchFilter(val, AppAssessments.QuestionFilter.READING_QUESTIONS)
      );
      const listening_questions_response = response.filter(
        (val: AppAssessments.AssessmentQuestionnaires) =>
          matchFilter(val, AppAssessments.QuestionFilter.LISTENING_QUESTIONS)
      );
      const normalizeOptions = (opts: unknown) => {
        if (typeof opts === "string") {
          try {
            return JSON.parse(opts) as AppAssessments.QuestionOptions;
          } catch {
            return opts;
          }
        }
        return opts;
      };
      const listening_questions_final_response = listening_questions_response
        .sort(
          (
            a: AppAssessments.AssessmentQuestionnaires,
            b: AppAssessments.AssessmentQuestionnaires
          ) => {
            const orderA =
              (normalizeOptions(a.question_options) as { order?: number })
                ?.order ?? 0;
            const orderB =
              (normalizeOptions(b.question_options) as { order?: number })
                ?.order ?? 0;
            return orderA - orderB;
          }
        )
        .map((val: AppAssessments.AssessmentQuestionnaires) => ({
          ...val,
          question_options: normalizeOptions(
            val.question_options
          ) as AppAssessments.QuestionOptions,
          answer: null
        }));
      const speak_final_response = speak_response
        .sort(
          (
            a: AppAssessments.AssessmentQuestionnaires,
            b: AppAssessments.AssessmentQuestionnaires
          ) => {
            return (
              (a.question_options?.order ?? 0) -
              (b.question_options?.order ?? 0)
            );
          }
        )
        .map((val: AppAssessments.AssessmentQuestionnaires) => {
          return {
            ...val,
            question_options: normalizeOptions(
              val.question_options
            ) as AppAssessments.QuestionOptions,
            answer: null
          };
        });
      const mcqs_final_response = mcqs_response
        .sort(
          (
            a: AppAssessments.AssessmentQuestionnaires,
            b: AppAssessments.AssessmentQuestionnaires
          ) => {
            return (
              (a.question_options?.order ?? 0) -
              (b.question_options?.order ?? 0)
            );
          }
        )
        .map((val: AppAssessments.AssessmentQuestionnaires) => {
          return {
            ...val,
            answer: null
          };
        });
      const write_final_response = written_response
        .sort(
          (
            a: AppAssessments.AssessmentQuestionnaires,
            b: AppAssessments.AssessmentQuestionnaires
          ) => {
            return (
              (a.question_options?.order ?? 0) -
              (b.question_options?.order ?? 0)
            );
          }
        )
        .map((val: AppAssessments.AssessmentQuestionnaires) => {
          return {
            ...val,
            answer: null
          };
        });

      const reading_questions_final_response = reading_questions_response
        .sort(
          (
            a: AppAssessments.AssessmentQuestionnaires,
            b: AppAssessments.AssessmentQuestionnaires
          ) => {
            return (
              (a.question_options?.order ?? 0) -
              (b.question_options?.order ?? 0)
            );
          }
        )
        .map((val: AppAssessments.AssessmentQuestionnaires) => {
          return {
            ...val,
            answer: null
          };
        });
      const filter =
        assessment.assessment_type == "english_v3"
          ? ViewAssessmentsOptions.MCQS
          : assessment.assessment_type == "english_v4" ||
            assessment.assessment_type == "english_v5" ||
            assessment.assessment_type == "english_genx_easy_v1" ||
            assessment.assessment_type == "english_genx_medium_v1" ||
            assessment.assessment_type == "english_technician_v1" ||
            assessment.assessment_type == "english_lead_technician_v1" ||
            assessment.assessment_type == "english_assistant_engineer_v1"
          ? ViewAssessmentsOptions.READING_QUESTIONS
          : ViewAssessmentsOptions.SPEAKING;
      // Debug: verify assessment and question counts (helps when Listening is missing locally)
      console.log("[English Assessment] Loaded", {
        assessment_id: assessment.id,
        assessment_type: assessment.assessment_type,
        total_questions: response.length,
        reading: reading_questions_response.length,
        listening: listening_questions_response.length,
        writing: written_response.length,
        speaking: speak_response.length,
        mcqs: mcqs_response.length
      });

      if (
        assessment.assessment_type === "english_v4" ||
        assessment.assessment_type === "english_v5" ||
        assessment.assessment_type === "english_genx_easy_v1" ||
        assessment.assessment_type === "english_genx_medium_v1" ||
        assessment.assessment_type === "english_technician_v1" ||
        assessment.assessment_type === "english_lead_technician_v1" ||
        assessment.assessment_type === "english_assistant_engineer_v1"
      ) {
        // Use allowed_time from assessment object (in seconds) for V4
        const totalTimer = assessment.allowed_time;

        // Count total questions across all sections
        let totalQuestions = 0;
        totalQuestions += speak_final_response.length;
        totalQuestions += reading_questions_final_response.length;
        totalQuestions += written_response.length;
        totalQuestions += listening_questions_final_response.length;
        totalQuestions += mcqs_final_response.length;

        console.log({ totalTimer, totalQuestions });

        localStorage.setItem(
          "englishAssessmentTotalTime",
          totalTimer.toString()
        );
        localStorage.setItem("totalQuestions", totalQuestions.toString());
      }

      this.set_app_eng_assessments({
        speaking: speak_final_response,
        speaking_completed: false,
        written: write_final_response,
        mcqs: mcqs_final_response,
        listening_questions: listening_questions_final_response,
        reading_questions: reading_questions_final_response,
        filter,
        cancelled: false,
        completed: false,
        current_question_index: 0,
        id: assessment.id,
        assessment_type: assessment.assessment_type
      });
      this.data_loading = false;
    },
    /** Call when leaving assessment without completing so re-entry requires admin approval again. */
    async requestResumeOnLeave() {
      if (
        this.assessment_completed ||
        !this.term_condition_accepted ||
        !this.assessment_id
      )
        return;
      try {
        await request_assessment_resume(this.assessment_id);
      } catch (_e) {
        // non-blocking
      }
    },
    showLeaveConfirmation(): Promise<boolean> {
      return new Promise((resolve) => {
        this.leave_dialog_resolve = resolve;
        this.leave_dialog_visible = true;
      });
    },
    onLeaveDialogStay() {
      this.leave_dialog_visible = false;
      if (this.leave_dialog_resolve) {
        this.leave_dialog_resolve(false);
        this.leave_dialog_resolve = null;
      }
    },
    onLeaveDialogLeave() {
      this.leave_dialog_visible = false;
      if (this.leave_dialog_resolve) {
        this.leave_dialog_resolve(true);
        this.leave_dialog_resolve = null;
      }
    }
  },
  async beforeRouteLeave(to, from, next) {
    const win = window as unknown as { __hcmsLoggingOut?: boolean };
    const isLoggingOut = win.__hcmsLoggingOut;

    // Skip the dialog if we are doing a programmatic redirect because the
    // assessment was already completed / submitted. In those cases the user
    // was never in an active session on this visit.
    const isRedirecting = this.assessment_already_completed;

    const justSubmitted = Boolean(
      (window as unknown as { __englishAssessmentSubmitted?: boolean })
        .__englishAssessmentSubmitted
    );

    const assessmentIsActive =
      this.term_condition_accepted &&
      !this.assessment_cancelled &&
      !justSubmitted;

    if (
      !isRedirecting &&
      assessmentIsActive &&
      !isLoggingOut &&
      !this.leave_dialog_confirmed
    ) {
      const confirmed = await this.showLeaveConfirmation();
      if (!confirmed) {
        next(false);
        return;
      }
      this.leave_dialog_confirmed = true;
    }

    // Treat the assessment as "submitted" ONLY when the explicit submit flow
    // has run and marked this navigation as a submission. A true Vuex
    // `assessment_completed` flag on its own is not enough – leaving during
    // any phase without that explicit submit must go through the
    // Admin-approval path.
    if (justSubmitted) {
      localStorage.removeItem("eng-terms-accepted");
      localStorage.removeItem("stage");
    } else {
      await this.requestResumeOnLeave();
    }
    next();
  },
  async beforeRouteEnter(to, from, next) {
    console.log("Entering");

    // Clear any stale "just submitted" flag from previous visits so that a
    // fresh attempt is always treated as in-progress until the explicit
    // submit flow runs again.
    const win = window as unknown as {
      __englishAssessmentSubmitted?: boolean;
    };
    delete win.__englishAssessmentSubmitted;

    // localStorage.removeItem("stage");
    // Resolve English assessment in the same way as getEnglishAssessmentId()
    // so that the route guard and the in-component init() agree on which
    // assessment ID to use.
    const app_assessments = store.getters[
      `common/${APP_ASSESSMENTS}`
    ] as AppAssessments.Assessment[];
    const user = store.getters[`auth/${GET_USER_DETAILS}`] as {
      persona?: string | null;
    };
    const assessment_setting = resolveEnglishAssessmentTypeByPersona(
      user?.persona,
      store.getters[`auth/${GET_COMPANY_DETAILS}`].storage_path
        ?.assessment_setting?.candidates_english
    );
    const assessment_setting_id = Number(assessment_setting);
    const has_numeric_assessment_setting = Number.isFinite(
      assessment_setting_id
    );

    let english_assessment = app_assessments.find(
      (val: AppAssessments.Assessment) =>
        val.assessment_type === assessment_setting ||
        (has_numeric_assessment_setting && val.id === assessment_setting_id)
    );

    if (!english_assessment) {
      const englishAssessments = app_assessments.filter(
        (val: AppAssessments.Assessment) =>
          val.assessment_name?.toLowerCase().includes("english") ||
          val.assessment_type?.toLowerCase().includes("english")
      );
      if (englishAssessments.length > 0) {
        const preferOrder = ["english_v5", "english_v4", "english_v3"];
        english_assessment =
          preferOrder
            .map((t) =>
              englishAssessments.find(
                (a: AppAssessments.Assessment) => a.assessment_type === t
              )
            )
            .find(Boolean) ||
          [...englishAssessments].sort((a, b) => b.id - a.id)[0];
      }
    }

    if (!english_assessment && app_assessments.length > 0) {
      english_assessment = app_assessments[0];
    }

    // Check if english assessment is attempted or not
    if (english_assessment) {
      const assessment_exist = await store.dispatch(
        `common/${APP_ASSESSMENT_RESULT}`,
        {
          assessment_id: english_assessment.id
        }
      );

      const status = assessment_exist?.status;
      const exist = assessment_exist as AssessmentResultWithScore | undefined;
      const hasScore = Boolean(
        exist?.overall_score ?? exist?.result?.overall_score
      );
      const isInProgress =
        status === AppAssessments.AppAssessmentStatus.IN_PROGRESS;
      const stuckNoScore =
        status === AppAssessments.AppAssessmentStatus.RESULT_PROCESSING &&
        !hasScore;

      // Completed / failed / cancelled etc. → abort entry and redirect.
      // Using next(false) ensures the component never mounts, so
      // beforeRouteLeave is never triggered and the Leave modal cannot appear.
      if (assessment_exist && !isInProgress && !stuckNoScore) {
        store.commit(
          ROOT_NOTIFICATION,
          i18n.t("assessments.completed-already")
        );
        // Clear the lingering terms-accepted flag so that if the component
        // somehow mounts anyway, term_condition_accepted initialises to false.
        localStorage.removeItem("eng-terms-accepted");
        localStorage.removeItem("stage");
        if (router.currentRoute.name !== "candidate-dashboard") {
          await router.push("/candidate/dashboard");
        }
        // Always block entry and send the user back to the dashboard.
        // This mirrors the "awaiting admin approval" flow so that in both
        // cases the candidate is never put into an active assessment view.
        return next(false);
      } else if (assessment_exist && isInProgress) {
        const adminStatus =
          (assessment_exist as AssessmentResultWithScore).resume_admin_status ??
          null;

        if (adminStatus === "APPROVED") {
          // Admin approved resume – track resume/start event
          await store.dispatch(
            `common/${MARK_ASSESSMENT_RESUMED}`,
            english_assessment.id
          );
        } else {
          // No approval yet: request resume and block entry
          try {
            await request_assessment_resume(english_assessment.id);
          } catch (_e) {
            // ignore – still block direct resume
          }
          store.commit(
            ROOT_NOTIFICATION,
            i18n.t("assessments.resume-awaiting-approval")
          );
          localStorage.removeItem("eng-terms-accepted");
          localStorage.removeItem("stage");
          if (router.currentRoute.name !== "candidate-dashboard") {
            await router.push("/candidate/dashboard");
          }
          // Clear the flag here too for the IN_PROGRESS + no-approval case,
          // and always send the user back to the dashboard. Using next(false)
          // ensures this component never mounts for "awaiting admin approval"
          // visits, so the Leave Assessment dialog cannot appear.
          return next(false);
        }
      }
    }
    next();
  },
  beforeDestroy() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
});
